import React, {
    useContext,
    createContext,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';

import { SOLD_OUT_STATUS } from 'app/utils/dish';
import { PROMOCODE_TYPES_PERCENT } from 'app/const/promo';

import { MenuDishesContext } from './PromoCheckoutMenuDishesProvider';
import { ConfigContext } from './ConfigProvider';
import { FiltersContext } from './FiltersProvider';
import { BasketContext } from './BasketProvider';


const getDishLayout = (category, dish) => {
    const { layout, enabledAdditionalDishes } = category;
    const defaultLayout = layout || 'default';
    if (enabledAdditionalDishes && enabledAdditionalDishes.includes(dish.id)) {
        return 'upsell';
    }
    return defaultLayout;
};

/*
    Разбиваем строку вида "1,3 кг" / "150г" / "15 мин" на числовую часть и единицу,
    чтобы единица не попадала в span.num и не ломала шрифт.
*/
const splitValueAndUnit = (value) => {
    if (value === null || value === undefined) {
        return { value: '', unit: '' };
    }

    const str = String(value).trim();
    const match = str.match(/^([\d.,\s\-–]*\d)\s*(.*)$/);

    if (!match) {
        return { value: str, unit: '' };
    }

    return { value: match[1].trim(), unit: match[2].trim() };
};

/*
    ny_content приходит с бэкенда строкой вида:
    "Томлёный утиный ножки конфи&150г$Готовое мясо птицы&150г$"
    Преобразуем её в формат таблицы "Что в наборе?"
*/
const parseNyContent = (nyContent) => {
    if (!nyContent || typeof nyContent !== 'string') {
        return null;
    }

    const list = nyContent
        .split('$')
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => {
            const [name, rawAmount] = item.split('&').map((part) => part.trim());
            const { value: amount, unit } = splitValueAndUnit(rawAmount);
            return { name, amount, unit };
        })
        .filter((item) => item.name);

    if (!list.length) {
        return null;
    }

    return [{ list }];
};

const formatNyDetail = (nyDetail) => {
    if (!nyDetail || typeof nyDetail !== 'string') {
        return null;
    }

    const paragraphs = nyDetail
        .replace(/\\r\\n|\\n|\\r/g, '\n')
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean)
        .map((paragraph) => paragraph.replace(/\n/g, '<br />'));

    if (!paragraphs.length) {
        return null;
    }

    return paragraphs
        .map((paragraph) => `<p class="ny__dishes-details__content paragraph">${paragraph}</p>`)
        .join('');
};

const mapDishesToData = (options) => {
    const {
        dishes,
        dishCategories,
        configDishesData,
        periodConfig,
        discountConditions,
        applyPromoDiscountToDishes,
        pageId,
    } = options;

    const categories = Object.values(dishCategories);

    let vinnayaTarelkaOccurrence = 0;
    let currentVinnayaTarelkaOccurrence = null;

    const dishesArray = dishes.map((dish) => {
        let categoryId = null;
        currentVinnayaTarelkaOccurrence = null;

        if (periodConfig) {
            const periodDishes = periodConfig.dishes;

            const entries = Object.entries(periodDishes);
            entries.forEach(([cid, dishIds]) => {
                if (dishIds.includes(dish.id)) {
                    categoryId = cid;
                }
            });
        }

        /*
            TODO: сделать нормальную возможность иметь один диш в двух категориях
        */
        if (dish.id === '3433' && pageId !== 'christmas' && pageId !== 'old_ny') {
            currentVinnayaTarelkaOccurrence = vinnayaTarelkaOccurrence;
            const cats = ['set', 'niceStuff', 'salads'];
            categoryId = cats[currentVinnayaTarelkaOccurrence] || cats[cats.length - 1];
            vinnayaTarelkaOccurrence += 1;
        }

        let category = categoryId
            ? dishCategories[categoryId]
            : categories.find((c) => {
                if (c.enabledDishes) {
                    const { enabledDishes, enabledAdditionalDishes } = c;
                    const ed1 = enabledDishes || [];
                    const ed2 = enabledAdditionalDishes || [];
                    const ed = ed1.concat(ed2);
                    return ed.includes(dish.id);
                }
                if (c.manageCategories) {
                    return c.manageCategories.includes(String(dish.categoryId));
                }
                if (c.enabledDishesByTag) {
                    const dishHasTag = dish.tags.find((tag) => tag.id === c.enabledDishesByTag);
                    return dishHasTag;
                }
                return false;
            });

        if (!category) {
            [category] = categories;
        }

        const configData = configDishesData[dish.id] || {};

        const isPercentPromo = PROMOCODE_TYPES_PERCENT.includes(discountConditions.discountType);
        const isNeededApplyPromoToPrice = isPercentPromo && applyPromoDiscountToDishes;

        // ny_full_price с бэкенда (цена без скидки) имеет приоритет над конфигом
        const originalPrice = dish.ny_full_price || configData.originalPrice || dish.price;
        const dishCountStep = dish.portions[0];
        const maxPortion = dish.portions[dish.portions.length - 1];
        const priceWithStep = dishCountStep === 1 ? String(dish.price) : `${dishCountStep} x ${dish.price}`;
        const originalPriceWithStep = dishCountStep === 1 ? String(originalPrice) : `${dishCountStep} x ${originalPrice}`;

        const price = isNeededApplyPromoToPrice
            ? dish.price * ((100 - discountConditions.discountValue) / 100)
            : dish.price;

        const hasNyName = Boolean(dish.ny_name);
        const hasNyDescription = Boolean(dish.ny_description);
        const hasNyMass = Boolean(dish.ny_mass);
        const hasNyTime = Boolean(dish.ny_time);

        // отделяем число от единицы, чтобы единица не попадала в span.num (шрифт граммовок/времени)
        const nyMass = splitValueAndUnit(dish.ny_mass);
        const nyTime = splitValueAndUnit(dish.ny_time);

        const configDetailContent = configData.detailContent || {};
        const nyContentTable = parseNyContent(dish.ny_content);

        const detailContent = {
            howMuch: null,
            howLong: null,
            ...configDetailContent,
        };

        // бэкенд в приоритете: ny_detail - длинное описание, ny_content - "Что в наборе?",
        // ny_cook_time - "Сколько готовить?", ny_total_volume - "Сколько получится?"
        const nyDetail = formatNyDetail(dish.ny_detail);
        if (nyDetail) {
            detailContent.content = nyDetail;
            detailContent.contentEn = nyDetail;
        }
        if (nyContentTable) {
            detailContent.table = nyContentTable;
            detailContent.tableEn = nyContentTable;
        }
        if (dish.ny_cook_time) {
            const nyCookTime = `<div class="ny__dishes-details__content paragraph">${dish.ny_cook_time}</div>`;
            detailContent.howLong = nyCookTime;
            detailContent.howLongEn = nyCookTime;
        }
        if (dish.ny_total_volume) {
            const nyTotalVolume = `<div class="ny__dishes-details__content paragraph">${dish.ny_total_volume}</div>`;
            detailContent.howMuch = nyTotalVolume;
            detailContent.howMuchEn = nyTotalVolume;
        }

        if (!detailContent.content) {
            detailContent.content = [{ text: dish.composition }];
        }
        if (!detailContent.table && configDetailContent.content) {
            detailContent.composition = dish.composition;
        }

        const {
            imageUrl, imageUrlDesktop, imageUrlDesktopDetails, imageUrlMobileDetails, verticalImage,
        } = configData;

        const imagesData = {
            imageUrl: imageUrl || dish.previewMobileImage,
            imageUrlDesktop: imageUrlDesktop || dish.previewImage,
            imageUrlDesktopDetails: imageUrlDesktopDetails || dish?.wideImage || dish?.previewImage,
            imageUrlMobileDetails: imageUrlMobileDetails || null,
            verticalImage: verticalImage || dish.verticalImage,
        };

        const weight = (() => {
            if (hasNyMass) {
                return nyMass.value;
            }
            if (configData.weight !== undefined) {
                return configData.weight;
            }
            return dishCountStep === 1 ? dish.weight : `${dishCountStep} x ${dish.weight}`;
        })();

        const time = (() => {
            if (hasNyTime) {
                return nyTime.value;
            }
            if (configData?.time) {
                return configData.time;
            }
            if (configData?.hour && configData?.minutes) {
                return '';
            }
            return dish.cooking_time / 60;
        })();

        const { orderNumber } = configData;
        let currentOrderNumber = orderNumber;
        let currentOrderNumberMobile = configData.orderNumberMobile;

        if (currentVinnayaTarelkaOccurrence !== null) {
            const orders = [4, 1, 4]; // set: 4, niceStuff: 1, salads: 4
            currentOrderNumber = orders[currentVinnayaTarelkaOccurrence] ?? orders[orders.length - 1];
            currentOrderNumberMobile = currentOrderNumber;
        }

        return {
            layout: getDishLayout(category, dish),
            priority: configData.priority || dish.priority,
            orderNumber: currentOrderNumber,
            orderNumberMobile: currentOrderNumberMobile,
            isWeightTimeHidden: configData.isWeightTimeHidden,
            isTimeHidden: configData.isTimeHidden,
            hideWeightOnDesktop: configData.hideWeightOnDesktop,
            category: category.id,
            categoryId: dish.categoryId, // категория от бэкенда
            id: dish.id,
            parentId: dish.parentId.toString(),
            visibleStatus: dish.visibleStatus,
            isSoldOut: dish.visibleStatus === SOLD_OUT_STATUS,
            dishCountStep,
            maxPortion,
            // ny_name с бэкенда имеет приоритет над конфигом
            title: hasNyName ? [dish.ny_name] : (configData.title || [
                dish.title,
                dish.caption,
            ]),
            titleEn: configData.titleEn ? configData.titleEn : [
                dish.titleEn || dish.title,
                dish.captionEn || dish.caption,
            ],
            titleForSmallCards: configData?.titleForSmallCards || configData?.title,
            // ny_description с бэкенда имеет приоритет над конфигом
            description: hasNyDescription ? dish.ny_description : configData.description,
            descriptionEn: configData.descriptionEn,
            partnersComment: dish.partnersComment,
            tags: dish?.tags,

            buttonText: configData.buttonText,
            buttonTextAction: configData.buttonTextAction,
            typeformWidgetId: configData.typeformWidgetId,
            linkUrl: configData.linkUrl,
            imageLinkUrl: configData.imageLinkUrl,
            isNovelty: configData.isNovelty,
            metaLabel: configData.metaLabel || null,

            weightValue: dish.weight,
            weight,
            // единица ny_mass идёт в weightText; если её нет - берём из конфига
            weightText: hasNyMass ? (nyMass.unit || configData.weightText || 'г') : (configData.weightText || 'г'),
            weightTextEn: hasNyMass ? (nyMass.unit || configData.weightTextEn || 'g') : (configData.weightTextEn || 'g'),
            time,
            hour: hasNyTime ? undefined : (configData?.hour || undefined),
            minutes: hasNyTime ? undefined : (configData?.minutes || undefined),
            hourText: hasNyTime ? '' : (configData?.hourText || ''),
            minutesText: hasNyTime ? '' : (configData?.minutesText || ''),
            timeText: hasNyTime ? (nyTime.unit || configData.timeText || 'минут') : (configData.timeText || 'минут'),
            timeTextEn: hasNyTime ? (nyTime.unit || configData.timeTextEn || 'minutes') : (configData.timeTextEn || 'minutes'),
            discountPriceWithStep: priceWithStep,
            originalPriceWithStep,
            price,
            portions: dish.portions,
            originalPrice: dish.price,

            detailContent,
            nutrition: dish.nutrition,

            ...imagesData,
            slides: configData.slides || [],
            triggerAgeGate: Boolean(configData.triggerAgeGate),

            giftWrapId: configData.giftWrapId,
        };
    });

    return dishesArray;
};

const extendSetsData = (options) => {
    const { dishes, configDishesData } = options;

    const extended = Object.values(configDishesData).map((setData) => {
        /*
            TODO: вместо этого должно быть поиск единственного диша в массиве дишей
        */
        if (!setData.dishses) return setData;

        const setDishes = dishes.filter(({ id }) => setData.dishes.includes(id));

        const dishCountStep = setDishes[0].portions[0];
        const maxPortion = setDishes[0].portions[setDishes[0].portions.length - 1];

        const weight = setDishes.reduce((acc, d) => d.weight + acc, 0);
        const time = Math.max(...setDishes.map((d) => d.cooking_time / 60));
        const price = setDishes.reduce((acc, d) => d.price + acc, 0);

        const { detailContent } = setData;

        return {
            ...setData,
            dishCountStep,
            maxPortion,
            weight: `${dishCountStep} x ${weight}`,
            weightText: 'г',
            time,
            timeText: 'минут',
            discountPriceWithStep: `${dishCountStep} x ${price}`,
            originalPriceWithStep: `${dishCountStep} x ${price}`,
            price,
            detailContent: {
                ...detailContent,
                table: setDishes.map((d) => {
                    const title = `${d.title} ${d.caption}`;
                    return {
                        title: title.replace('Mondoro', '').trim(),
                        list: [{ name: d.composition }],
                    };
                }),
            },
        };
    });

    return extended;
};

export const PromoCheckoutPatchedDishesContext = createContext({
    dishesArray: [],
});


export function PromoCheckoutMenuDishesAdapter(props) {
    const { children } = props;

    const { period } = useContext(FiltersContext);

    const {
        isDishesDataInPriority,
        dishCategories,
        dishesData: configDishesData,
        periods: periodsConfig,
        isOnlyLocalDishData,
        applyPromoDiscountToDishes,
        pageId,
    } = useContext(ConfigContext);

    const {
        dishes: filteredDishes,
    } = useContext(MenuDishesContext);

    const {
        basket: { discount_conditions: discountConditions },
    } = useContext(BasketContext);

    const dishesArray = isDishesDataInPriority
        ? extendSetsData({ dishes: filteredDishes, configDishesData })
        : mapDishesToData({
            dishes: filteredDishes,
            dishCategories,
            configDishesData,
            periodConfig: periodsConfig ? periodsConfig.find((pc) => pc.period === period) : null,
            discountConditions,
            applyPromoDiscountToDishes,
            pageId,
        });

    const contextValue = useMemo(() => {
        const d = isOnlyLocalDishData
            ? Object.values(configDishesData)
            : dishesArray;
        return {
            dishesArray: d,
        };
    }, [dishesArray, isOnlyLocalDishData, configDishesData]);


    return (
        <PromoCheckoutPatchedDishesContext.Provider value={contextValue}>
            {children}
        </PromoCheckoutPatchedDishesContext.Provider>
    );
}

PromoCheckoutMenuDishesAdapter.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
};
